<template>
  <div class="lego-table" ref="legoTableRef">
    <div class="lego-table__top">
      <!-- Table Title -->
      <div class="lego-table__title" v-if="title">
        <label class="lego-table__title__label">{{ title }}</label>
        <span class="lego-table__title__desc" v-if="titleDesc">{{ titleDesc }}</span>
      </div>
      <!-- Table Control -->
      <div class="lego-table__control" :class="controlClass">
        <div class="lego-table__counter" v-if="!noControl" :class="{ 'left-divider': title }">
          <!--
                  $ver: v0.1.0
                  총 항목 수, 페이지 당 항목 수를 보여주는 영역입니다. 다른 컨텐츠로 대체할 수 있습니다.
                  #This area shows the total number of items and the number of items per page. You can replace it with other content.
          -->
          <slot name="counter-instead">
            <span class="lego-table__counter__total">
              {{ totalText || t('label.total') }}
              <b>{{ $filters.comma(totalCount) }}</b>
            </span>
            <div
              class="lego-table__action-selected left-divider"
              v-if="selected && isActionSelect && !singleSelect && !noSelectedShow"
            >
              <span>{{ selected.length }}{{ selectedText || t('label.selected') }}</span>
              <span v-if="deselectBtn" class="deselect-btn" @click="clearSelected(true)">
                {{ t('label.deselection') }}
              </span>
            </div>
            <div class="left-divider" v-if="!noPage">
              <lego-dropdown
                class="lego-table__counter__per-page"
                @update:modelValue="changePage"
                :items="$_perPageItems"
                :disabled="items.length === 0"
                :itemWidth="98"
                v-model="rowPerPage"
                borderLess
              >
                <template v-slot:itemText="itemTextScope">
                  <b>{{ itemTextScope.item && itemTextScope.item.value }}</b>
                  {{ perPageText || t('label.perPage') }}
                </template>
              </lego-dropdown>
            </div>
          </slot>
        </div>
        <div class="lego-table__action">
          <!--
                  $ver: v0.1.0
                  테이블 우측 상단에 위치하는 영역입니다. 일반적으로 테이블의 추가, 수정, 삭제에 대한 버튼이 위치합니다.
                  #This area is in the upper right corner of the table. In general, there are buttons for adding, modifying, and deleting tables.
          -->
          <slot name="action">
            <template v-if="selectActionControl && (singleSelect || multiSelect)">
              <lego-button sm @click="addItem">{{ t('button.add') }}</lego-button>
              <lego-button sm :disabled="modelValue.length !== 1" @click="modifySelectedItem">
                {{ t('button.modify') }}
              </lego-button>
              <lego-button sm :disabled="modelValue.length === 0" @click="removeSelectedItem">
                {{ t('button.remove') }}
              </lego-button>
            </template>
          </slot>
        </div>
        <div class="lego-table__extra-action" v-if="$slots['extra-action']">
          <!--
                  $ver: v0.1.1
                  테이블 우측 상단에 위치하는 영역입니다. action 영역에 추가로 영역이 더 필요할 경우 사용합니다.
                  #This area is in the upper right corner of the table. Use if you need more areas in addition to the action area.
          -->
          <slot name="extra-action" />
        </div>
      </div>
    </div>
    <div class="lego-table__top-second">
      <div class="lego-table__extra-action-row" v-if="$slots['extra-action-row']">
        <!--
                $ver: v0.1.1
                테이블 우측 상단에 위치하는 영역입니다. action 영역에 추가로 영역이 더 필요할 경우 사용합니다.
                #This area is in the upper right corner of the table. Use if you need more areas in addition to the action area.
        -->
        <slot name="extra-action-row" />
      </div>
    </div>
    <div ref="tableHeader" class="header-table">
      <table
        class="lego-table__area"
        @mousemove="headerDragOver"
        @dragover="headerDragOver"
        @mouseup="headerDragEnd"
        @dragend="headerDragEnd"
        ref="cloneTable"
      >
        <template v-if="colgroup">
          <colgroup>
            <template v-for="(header, index) in dumpHeaders">
              <col v-if="!header.children" :key="index" :width="header.width" />
            </template>
          </colgroup>
        </template>
        <!-- Header -->
        <thead class="lego-table__header" ref="tableHead" :class="headerClass">
          <!--
                    $ver: v0.1.0
                    table 태그 아래 thead 태그 영역입니다. 기본적으로 자동 생성되나, customHeader 속성으로 tr/th 태그로 커스텀 할 수 있습니다.
                    #Thead tag area below the table tag. It is automatically created by default, but can be customized with the tr / th tag with the customHeader attribute.
          -->
          <slot v-if="customHeader" name="header" :attr="headerAttr" />
          <tr class="lego-table__header__row" v-else :style="{ height: headerHeight + 'px' }">
            <!-- @mouseleave="headerMouseLeave" -->
            <th
              v-if="selectAll"
              class="lego-table__header-checker"
              :style="{ width: selectAllWidth + 1 + 'px' }"
            >
              <lego-checkbox
                small
                :disabled="items.length === 0"
                :checked="selectAllChecked"
                :indeterminate="selectAllChecked && selectAllCheckedIcon === 'indeterminate'"
                @click="clickSelectAll"
              />
            </th>
            <th
              v-if="singleSelect"
              class="lego-table__header-checker"
              :style="{ width: selectAllWidth + 1 + 'px' }"
            ></th>
            <th
              v-for="(header, hIdx) in dumpHeaders"
              :key="hIdx"
              :style="thStyle(header)"
              :class="header.displayFrom ? 'lego-table__col__from-' + header.displayFrom : ''"
            >
              <span :style="{ 'line-height': headerContentHeight }">
                {{ header.text }}
                <span class="lego-table__icon-required" v-if="header.required">*</span>
                <lego-icon
                  v-if="header.sortable"
                  class="lego-table__icon-sort"
                  iconType="df"
                  :icon="sortIcon(hIdx)"
                  :size="16"
                  :class="{ 'lego-table__icon-sort--selected': sortIdx == hIdx }"
                  @click.stop="sortIconClick(hIdx)"
                />
                <lego-icon
                  v-if="header.filterable"
                  class="lego-table__icon-filter"
                  iconType="df"
                  icon="filter"
                  :size="16"
                  @click.stop="filterIconClick(hIdx)"
                />
              </span>
              <lego-icon-wrapper class="lego-table__hidden-icon">
                <lego-icon
                  icon-size="16px"
                  icon="change_width"
                  style="cursor: pointer"
                  @click="header.displayFrom = ''"
                />
              </lego-icon-wrapper>
              <div
                class="lego-table__header-resize"
                v-if="resizeColumn"
                :style="headerResizeBoxStyle(hIdx)"
                @dragstart="headerDragStart(hIdx, $event)"
                @mousedown="headerDragStart(hIdx, $event)"
              />
            </th>
            <th v-if="rowMenuItems.length > 0" style="width: 50px"></th>
          </tr>
          <tr style="height: 0 !important; min-height: 0 !important">
            <!--  -->
            <td class="lego-table__header__resizer-td">
              <div
                v-if="resizeColumn"
                v-show="toggleResize"
                class="lego-table__resize-line"
                :style="resizeLineStyle"
              />
            </td>
          </tr>
        </thead>
      </table>
    </div>
    <div class="lego-table__area-wrapper" ref="tableWrapper" :style="bodyStyle">
      <c-scroll :style="bodyScrollHeight" @scrolling="checkBodyScroll" @customClick="customClick">
        <table
          class="lego-table__area"
          @mousemove="headerDragOver"
          @dragover="headerDragOver"
          @mouseup="headerDragEnd"
          @dragend="headerDragEnd"
        >
          <!-- <colgroup> -->
          <!--
                      $ver: v0.1.0
                      table 태그 아래 colgroup 태그 영역입니다. 기본적으로 전체 체크, 각 컬럼의 헤더에 맞춰서 생성됩니다.
                      #The colgroup tag area below the table tag. By default, all checks are made to match the header of each column.
          -->
          <!-- <slot name="colgroup">
            <col v-if="selectAll" :width="selectAllWidth + 'px'"/>
            <col v-for="(header, hIdx) in dumpHeaders" :width="header.width" :class="header.displayFrom?'lego-table__col__from-'+header.displayFrom:''" :key="hIdx"/>
          </slot>
          </colgroup>-->
          <!-- Body -->
          <tbody class="lego-table__body table-extend-body" ref="tableBody" :class="bodyClass">
            <slot name="customRow" :attr="headerAttr" :rowAttr="rowAttr"></slot>
            <tr v-if="singleSelect && (multiSelect || selectAll)" class="lego-table__body__alert">
              <td :colspan="alertColspan">
                You can't use 'singleSelect prop' with multiSelect or selectAll prop
              </td>
            </tr>
            <template v-else v-for="(item, iIdx) in items" :key="item[itemId] + '_' + iIdx">
              <tr
                @click="(e) => onClickRow(item, 'row', iIdx, e)"
                :style="tableRowStyle"
                :id="item[itemId] + '_' + iIdx"
                :class="{
                  'lego-table__selected': isSelect && isSelected(item),
                  sortableRow: rowSortable,
                  'lego-table__sorted': item.flag === 'E',
                  'row-hover': rowHover,
                  disabled: item.disabled,
                  'row-border-bottom': scrollFixRow && items.length < scrollFixRow
                }"
              >
                <!--
                              $ver: v0.1.0
                              각 Row 영역입니다. 기본적으로 입력된 데이터에 따라 자동생성되나, <td> 태그를 활용하여 커스텀 할 수 있습니다.
                              #Each Row area. By default, it is automatically generated based on the data entered, but can be customized using the <td> tag.
                -->
                <slot
                  name="items"
                  :item="item"
                  :index="iIdx"
                  :attr="itemsAttr(item)"
                  :children="item.children"
                >
                  <!-- Default Cell -->
                  <td
                    v-if="multiSelect"
                    :class="[
                      'lego-table__cell-checker',
                      {
                        'cell-checker--disabeld': item.disabled || item.checkDisabled
                      }
                    ]"
                    :style="{ width: selectAllWidth + 'px' }"
                  >
                    <!-- 191202 backup <lego-checkbox small v-model="value" :value="item"/> -->
                    <lego-checkbox
                      small
                      :valueKey="valueKey"
                      :checked="selected"
                      @update:modelValue="(val) => onClickRowCheckbox(item, val)"
                      :value="item"
                      :disabled="item.disabled || item.checkDisabled"
                    />
                  </td>
                  <td
                    v-if="singleSelect"
                    :class="[
                      'lego-table__cell-checker',
                      {
                        'cell-checker--disabeld': item.disabled || item.checkDisabled
                      }
                    ]"
                    :style="{ width: selectAllWidth + 'px' }"
                  >
                    <lego-radio
                      small
                      :valueKey="valueKey"
                      @update:modelValue="(val) => onClickRowCheckbox(item, val)"
                      :checked="isSingleCheck(item)"
                      :disabled="item.disabled || item.checkDisabled"
                    />
                  </td>
                  <template v-for="(header, hIdx) in dumpHeaders">
                    <template v-if="!header.children">
                      <td
                        v-if="
                          !header.checker &&
                          (!header.itemsRowspan || calcRowspan(header.itemsRowspan, iIdx) > 0)
                        "
                        :class="
                          header.displayFrom ? 'lego-table__col__from-' + header.displayFrom : ''
                        "
                        :key="item[itemId] + '_' + iIdx + '_' + hIdx"
                        :style="{
                          width: `${header.width}px`,
                          textAlign: header.bodyAlign && header.bodyAlign,
                          borderBottom:
                            header.itemsRowspan &&
                            calcRowspan(header.itemsRowspan, iIdx) + iIdx === items.length &&
                            0
                        }"
                        :rowspan="header.itemsRowspan && calcRowspan(header.itemsRowspan, iIdx)"
                      >
                        <v-runtime-template
                          v-if="header.customCell"
                          :template="header.customCell(item[header.value], iIdx, item)"
                        />
                        <template v-else>
                          <div
                            v-if="header.tooltip"
                            class="value"
                            v-lego-tooltip="header.tooltip(item)"
                          >
                            {{
                              header.defaultValue !== null &&
                              header.defaultValue !== undefined &&
                              (item[header.value] === null ||
                                item[header.value] === undefined ||
                                item[header.value] === '')
                                ? header.defaultValue
                                : item[header.value]
                            }}
                          </div>
                          <div v-else class="value" v-lego-tooltip:ellipsis="item[header.value]">
                            {{
                              header.defaultValue !== null &&
                              header.defaultValue !== undefined &&
                              (item[header.value] === null ||
                                item[header.value] === undefined ||
                                item[header.value] === '')
                                ? header.defaultValue
                                : item[header.value]
                            }}
                          </div>
                        </template>

                        <lego-icon-wrapper class="lego-table__hidden-icon">
                          <lego-icon :size="16" icon-type="df" icon="more" />
                        </lego-icon-wrapper>
                      </td>
                    </template>
                  </template>
                  <!-- Default Cell END -->
                </slot>
                <td v-if="rowMenuItems.length > 0" style="width: 57px">
                  <lego-drop-holder
                    ref="rowMenuDropHolder"
                    :items="rowMenuItems"
                    :itemWidth="80"
                    menuDirection="right"
                    appendToBody
                    @click="(val) => rowMenuClick(item, val)"
                  >
                    <lego-icon :size="16" icon-type="df" icon="more" />
                  </lego-drop-holder>
                </td>
              </tr>
              <tr v-if="item.expand" :key="item[itemId] + '_' + iIdx + '_expand'">
                <td :colspan="alertColspan" class="lego-table__cell--expand">
                  <!--
                                  $ver: v0.1.0
                                  각 Row 가 펼침/닫힘 처리가 가능할 경우, 펼쳐지는 부분에 대한 컨텐츠 영역 입니다.
                                  #If each row can be expanded / closed, this is the content area for the expanded part.
                  -->
                  <slot name="expand" :item="item" />
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </c-scroll>
      <div
        v-if="items.length === 0 && !$slots['customRow']"
        class="lego-table__no-data"
        :style="noDataStyle"
      >
        <!-- <lego-icon iconType="df" icon="add_box_40" :iconSize="40" /> -->
        <img src="@/assets/img/scmp/no_data.png" />
        {{ noDataText || t('label.noData') }}
      </div>
    </div>
    <div v-if="customFooter" class="footer-table">
      <table class="lego-table__area">
        <tfoot class="lego-table__footer" ref="tableFoot">
          <slot name="footer" :attr="footerAttr" />
        </tfoot>
      </table>
    </div>
  </div>
</template>
<script>
import VRuntimeTemplate from 'vue3-runtime-template';
import i18nMixin from '@/mixins/i18n.mixin.js';
import _ from 'lodash';

export default {
  name: 'legoTable',
  mixins: [i18nMixin],
  emits: [
    'sort',
    'change',
    'customClick',
    'update:modelValue',
    'removeSelectedItem',
    'modifySelectedItem',
    'addItem',
    'rowMenuClick'
  ],
  components: {
    VRuntimeTemplate
  },
  props: {
    rowHeight: { type: [Number, String], default: 32 },
    headerHeight: { type: [Number, String], default: 28 },
    multiRowHeader: { type: Number, default: 0 },
    scrollMaxRow: { type: Number, default: null },
    scrollFixRow: { type: Number, default: null },
    customFooter: { type: Boolean, default: false },
    selectActionControl: { type: Boolean, default: true },
    selectedReset: { type: Boolean, default: false },
    rowMenuItems: { type: Array, default: () => [] },
    deselectBtn: { type: Boolean, default: true },
    noSelectedShow: { type: Boolean, default: false },
    rowspanTable: { type: Boolean, default: false },
    colgroup: { type: Boolean, default: false },
    valueKey: { type: String, default: '' }
  },
  data: () => ({
    hasTableScroll: false,
    setDefaultSort: false,
    rowHover: false,
    selectAllWidth: 54,
    default: {
      perPageItems: [
        // { text: '5', value: 5 },
        { text: '10', value: 10 },
        { text: '20', value: 20 },
        { text: '30', value: 30 },
        { text: '40', value: 40 },
        { text: '50', value: 50 }
      ]
    }
  }),
  mounted() {
    if (this.isSelect) {
      this.rowHover = true;
    }
    this.changePage();
  },
  watch: {
    modelValue: {
      deep: true,
      handler(val) {
        this.selected = val;
      }
    },
    items: {
      deep: true,
      handler() {
        this.selected = this.modelValue;
      }
    }
  },
  computed: {
    isSelect() {
      return this.$attrs['onClickRow'];
    },
    totalCount() {
      return this.pagination.totalItems || this.items.length;
    },
    headerContentHeight() {
      return '19px';
    },
    headerClass() {
      return [
        this.customHeader && 'lego-table__header-custom',
        this.multiRowHeader && 'lego-table__header-multi-row'
      ];
    },
    controlClass() {
      let clazz = [];
      if (this.reverseControl) {
        clazz.push('lego-table__control--reverse');
      }
      if (this.noPage) {
        clazz.push('lego-table__control--no-page');
      }
      return clazz;
    },
    footerAttr() {
      let styleObject = {};

      this.dumpHeaders.forEach((header) => {
        styleObject[header.value] = {
          style: {
            width: header.width ? header.width + 'px' : 'auto',
            textAlign: header.align && header.align
          },
          colspan: header.colspan
        };
      });

      return styleObject;
    },
    headerAttr() {
      let styleObject = {};
      if (this.multiSelect || this.singleSelect) {
        styleObject.checker = {
          style: {
            width: this.selectAllWidth + 1 + 'px'
          },
          rowspan: this.multiRowHeader
        };
      }

      this.dumpHeaders.forEach((header) => {
        styleObject[header.value] = {
          style: {
            width: header.width ? header.width + 'px' : 'auto',
            textAlign: header.headerAlign && header.headerAlign
          },
          colspan: header.colspan,
          rowspan: header.rowspan
        };
      });

      return styleObject;
    },
    rowAttr() {
      return {
        style: {
          height: this.rowHeight + 'px'
        },
        class: {
          'row-hover': this.rowHover
        }
      };
    },
    checkedLen() {
      const ableItems = this.items.filter((item) => !item.disabled && !item.checkDisabled);
      let selectedLen = 0;
      ableItems.forEach((item) => {
        if (this.valueKey) {
          let containItems = this.selected.filter(
            (selectedItem) => item[this.valueKey] === selectedItem[this.valueKey]
          );
          if (containItems.length > 0) {
            selectedLen += 1;
          }
        } else if (_.find(this.selected, item)) {
          selectedLen += 1;
        }
      });

      if (selectedLen === ableItems.length && ableItems.length > 0) {
        return 'all';
      } else if (selectedLen > 0) {
        return 'some';
      }
      return 0;
    },
    selectAllCheckedIcon() {
      if (this.checkedLen === 'all') return 'checked';
      else return 'indeterminate';
    },
    selectAllChecked() {
      return this.checkedLen !== 0;
    },
    bodyStyle() {
      const bodyStyle = {};

      if (this.items.length === 0 && !this.$slots['customRow']) {
        bodyStyle.borderTop = 0;
      }

      return bodyStyle;
    },
    bodyScrollHeight() {
      const bodyHeight = {};
      if (this.scrollMaxRow) {
        bodyHeight.maxHeight = `${this.scrollMaxRow * 32}px`;
      }

      if (this.scrollFixRow && this.items.length > 0) {
        bodyHeight.height = `${this.scrollFixRow * 32}px`;
      }

      return bodyHeight;
    },
    $_perPageItems() {
      let items = this.perPageItems || this.default.perPageItems;
      const perPageText = this.perPageText || this.t('label.perPage');

      items.forEach((item) => {
        item.text += perPageText;
      });

      return items;
    },
    bodyClass() {
      return {
        'column-border': this.rowspanTable
      };
    },
    tableRowStyle() {
      return {
        height: this.tableRowHeight
      };
    },
    noDataStyle() {
      let paginationHeight = this.noPage ? 0 : 42;
      return {
        height:
          this.scrollFixRow &&
          this.scrollFixRow * 32 + this.headerHeight + paginationHeight + 2 + 'px'
      };
    }
  },
  methods: {
    buildDumpHeader() {
      this.dumpHeaders = Object.assign([], this.headers);
      if (this.resizeColumn) {
        let lastIdx = this.dumpHeaders.length - 1;
        this.dumpHeaders[lastIdx].width = undefined;
      }

      if (!this.setDefaultSort) {
        //Set Default Sort
        this.dumpHeaders.forEach((header, index) => {
          if (header.desc || header.asc) {
            this.sortIdx = index;
          }
        });
      }
    },
    sortIcon(hIdx) {
      if (hIdx === this.sortIdx) {
        if (this.dumpHeaders[hIdx].desc) {
          return 'sort_up';
        } else {
          return 'sort_down';
        }
      } else {
        return 'sort';
      }
    },
    sortIconClick(hIdx) {
      this.setDefaultSort = true;

      if (this.sortableType === 'triple') {
        // sortableType (triple)
        if (this.dumpHeaders[hIdx].asc) {
          // asc -> desc
          this.dumpHeaders[hIdx].asc = false;
          this.dumpHeaders[hIdx].desc = true;
          this.sortIdx = hIdx;
        } else if (this.dumpHeaders[hIdx].desc) {
          // asc -> default
          this.dumpHeaders[hIdx].desc = false;
          this.sortIdx = -1;
        } else {
          // default -> asc
          this.dumpHeaders[hIdx].asc = true;
          this.sortIdx = hIdx;
        }
      } else {
        // sortableType (dual)
        this.dumpHeaders[hIdx].desc = !!this.dumpHeaders[hIdx].asc;
        this.dumpHeaders[hIdx].asc = !this.dumpHeaders[hIdx].asc;
        this.sortIdx = hIdx;
      }

      this.$emit('sort', this.dumpHeaders[hIdx]);
    },
    changePage(val) {
      if (val) {
        this.pagination.totalPages = Math.ceil(this.pagination.totalItems / val);
        this.pagination.rowsPerPage = val;

        this.$emit('change', val);
      } else {
        this.pagination.totalPages = Math.ceil(this.pagination.totalItems / this.rowPerPage);
      }
    },
    customClick(idx) {
      this.$emit('customClick', this.items[idx]);
    },
    thStyle(header) {
      let h = {};
      h['text-align'] = header.headerAlign ? header.headerAlign : 'center';
      if (header.width) {
        h['width'] = header.width + 'px';
      }
      return h;
    },
    itemsAttr() {
      let styleObject = {};
      if (this.multiSelect || this.singleSelect) {
        styleObject.checker = {
          style: {
            width: this.selectAllWidth + 'px'
          }
        };
      }

      this.dumpHeaders.forEach((header) => {
        styleObject[header.value] = {
          style: {
            width: header.width && header.width + 'px',
            textAlign: header.align && header.align
          }
        };
      });

      return styleObject;
    },
    calcRowspan(itemsRowspan, index) {
      let rowspanArray = [];

      itemsRowspan.forEach((item) => {
        rowspanArray[item.itemIndex] = item.rowspan;
        for (let i = item.itemIndex + 1; i < item.itemIndex + item.rowspan; i++) {
          rowspanArray[i] = 0;
        }
      });

      return rowspanArray[index] === undefined ? 1 : rowspanArray[index];
    },
    clearSelected(btn) {
      if (this.selectedReset || btn) {
        this.selected = [];
        this.$emit('update:modelValue', this.selected);
      }
    },
    clickSelectAll() {
      const ableItems = this.items.filter((item) => !item.disabled && !item.checkDisabled);
      if (this.checkedLen === 'all') {
        if (this.valueKey) {
          this.selected = this.selected.filter(
            (item) =>
              ableItems.filter((ableItem) => ableItem[this.valueKey] === item[this.valueKey])
                .length === 0
          );
        } else {
          this.selected = this.selected.filter((item) => !_.find(ableItems, item));
        }

        this.$emit('update:modelValue', this.selected);
      } else {
        this.selected = ableItems.reduce((acc, cur) => {
          if (this.valueKey) {
            if (acc.filter((accItem) => accItem[this.valueKey] === cur[this.valueKey]).length > 0) {
              return acc;
            } else {
              return [...acc, cur];
            }
          } else {
            if (_.find(acc, cur)) {
              return acc;
            } else {
              return [...acc, cur];
            }
          }
        }, this.selected);
        this.$emit('update:modelValue', this.selected);
      }
    },
    onClickRow(row, target, rowIndex, e) {
      if (this.rowMenuItems.length > 0) {
        const rowEl = this.$refs.tableWrapper.querySelectorAll('tr')[rowIndex];
        const dropholder = rowEl.querySelector('.lego-drop-holder');
        if (dropholder.contains(e.target) || dropholder === e.target) {
          return;
        }
      }
      if (target === 'row' && this.isSelect) {
        this.$attrs['onClickRow'](row);
      }

      if (target === 'expand') {
        this.onClickRowExpand(row);
      }
    },
    onClickRowCheckbox(row, value) {
      if (!this.multiSelect && !this.singleSelect) {
        return;
      }

      if (!this.multiSelect) {
        this.selected = [row];
      } else {
        let idx = _.findIndex(this.selected, { userSq: row.userSq });
        if (idx > -1) {
          this.selected = value.filter((valueItem) => valueItem.userSq !== row.userSq);
        } else {
          this.selected = value;
        }
      }

      this.$emit('update:modelValue', this.selected);
    },
    isSingleCheck(item) {
      if (this.valueKey) {
        return (
          this.modelValue.filter((valueItem) => valueItem[this.valueKey] === item[this.valueKey])
            .length > 0
        );
      } else {
        return _.find(this.modelValue, item) ? true : false;
      }
    },
    removeSelectedItem() {
      this.$emit('removeSelectedItem', this.modelValue);
    },
    modifySelectedItem() {
      this.$emit('modifySelectedItem', this.modelValue);
    },
    addItem() {
      this.$emit('addItem');
    },
    rowMenuClick(row, val) {
      this.$emit('rowMenuClick', row, val);
    },
    checkBodyScroll(e) {
      if (this.rowMenuItems.length > 0) {
        this.$nextTick(() => {
          this.items.forEach((item, index) => {
            this.$refs.rowMenuDropHolder[index].toggleItemOuter();
          });
        });
      }
      if (e.target.scrollLeft !== this.$refs.tableHeader.scrollLeft) {
        this.$refs.tableHeader.scrollLeft = e.target.scrollLeft;
      }
    }
  }
};
</script>
<style lang="scss" scoped>
.lego-table {
  overflow: visible;

  &__top {
    display: flex;
    align-items: center;
    margin-bottom: 8px;
    .lego-table__title {
      height: 32px;
    }

    .left-divider {
      &:before {
        position: relative;
        display: inline-block;
        margin: 0 8px;
        border-left: 1px solid $color--ui07;
      }
    }
  }

  .lego-table__control {
    height: inherit;
    flex: 1;
  }

  .lego-table__counter {
    margin-right: 8px;

    &__total {
      b {
        color: $color--primary;
      }
    }

    .lego-table__action-selected {
      display: flex;
      align-items: center;
      margin-right: 0;
      font-weight: $lego__font-weight--bold;

      .deselect-btn {
        color: $color--ui06;
        cursor: pointer;
        margin-left: 8px;
        font-weight: $lego__font-weight--normal;
        line-height: 18px;
        border-bottom: 1px solid $color--ui06;
      }
    }
  }

  &__counter__per-page {
    :deep.lego-dropdown__title {
      padding-left: 0;
      padding-right: 0;

      .lego-dropdown__title-icon {
        margin-left: 6px;
      }
      &:focus {
        &::before {
          display: none;
        }
      }
    }
  }

  .lego-table__area-wrapper {
    position: relative;
    overflow-y: auto;
    overflow-x: hidden;
    border-top: $lego-table__body__border-top;
    border-bottom: $lego-table__body__border-bottom;

    :deep .simplebar-offset {
      width: 100%;
    }
    :deep .simplebar-track {
      &.simplebar-vertical {
        background: $color--white;
      }
    }
  }

  .lego-table__area {
    border-collapse: separate;
    border-spacing: 0;
    width: 100%;
    table-layout: fixed;

    .lego-table__header {
      tr {
        &.lego-table__header__row {
          border-top: 0;
          border-bottom: 0;
        }
      }

      :deep th {
        border-right: $lego-table__column__border;
      }

      :deep .lego-table__icon-sort {
        font-size: 16px;
        vertical-align: middle;
        margin-top: -2px;

        &:not(.lego-table__icon-sort--selected) {
          color: $color--ui04;
        }
      }

      .lego-table__icon-required {
        color: $color--status--notification;
      }
    }

    .lego-table__header-custom {
      &.lego-table__header-multi-row {
        th[colspan] {
          border-bottom: $lego-table__contents__border-bottom;
        }

        th {
          height: 30px;
        }
      }
    }

    .lego-table__body {
      td {
        border-bottom: $lego-table__contents__border-bottom;
        padding-left: 19px;
        @include ellipsis;

        &:last-child {
          border-right: 0;
        }

        > a {
          color: $color--primary;

          &.link {
            text-decoration: underline;
            cursor: pointer;
          }
        }

        .value {
          @include ellipsis;
        }
      }

      tr {
        border-bottom: 0;

        &:last-child:not(.row-border-bottom) {
          td {
            border-bottom: 0;
          }
        }

        &:hover {
          background: none;
        }

        &.lego-table__selected {
          background: rgba($color--primary, 0.12);
        }

        &.row-hover {
          cursor: pointer;
          &:hover {
            background: rgba($color--primary, 0.04);
          }
        }

        .lego-table__cell-checker {
          pointer-events: none;
          text-overflow: inherit;

          &:not(.cell-checker--disabeld) {
            .lego-input {
              pointer-events: auto;
            }
          }
        }

        &.disabled {
          pointer-events: none;
        }
      }

      &.column-border {
        td {
          border-right: 1px solid $color--ui08;

          &:last-child {
            border-right: 0;
          }
        }
      }
    }
  }

  .header-table {
    position: relative;
    background-color: $lego-table__header__background-color;
    border-top: $lego-table__header__border-top;
    overflow: hidden;

    &:after {
      content: '';
      position: absolute;
      position: absolute;
      top: 0;
      right: 0;
      height: 100%;
      width: 1px;
      background: $lego-table__header__background-color;
    }
  }

  .lego-table__no-data {
    display: flex;
    justify-content: center;
    align-items: center;
    flex-direction: column;
    height: 96px;
    background: $color--ui12;
    border-top: 1px solid $color--ui05;
    color: $color--ui07;
    font-size: 14px;
    font-weight: $lego__font-weight--medium;

    :deep .lego-icon {
      color: rgba($color--ui01, 0.2);
    }
  }

  .footer-table {
    margin-top: -1px;
    background: $lego-table_footer_background-color;

    tr {
      border-bottom: $lego-table__contents__border-bottom;
      height: 32px;

      td {
        padding: 0 16px;
        @include font($size: 14px);
      }
    }
  }
}
</style>
<style lang="scss">
.lego-table {
  .lego-table__area {
    .lego-table__body {
      &.table-extend-body {
        > tr {
          border-bottom: 0;
          td {
            border-bottom: $lego-table__contents__border-bottom;
          }
          &.disabled {
            td {
              position: relative;
              &:after {
                position: absolute;
                top: 0;
                left: 0;
                content: '';
                background: rgba($color--white, 0.8);
                width: 100%;
                height: 100%;
              }
            }
          }

          &:last-child:not(.row-border-bottom) {
            td {
              border-bottom: 0;
            }
          }
        }
      }
    }
  }
}
</style>
